import React, { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { Spinner } from "reactstrap";
import Customers from "../../pages/Customers";
import { keys } from "../../query/keys";
import { getAllCustomers, getAllCustomersSearch } from "../../query/queryFunctions";
import debounce from 'lodash/debounce';

const CustomersContainer = () => {
  const [searchText, setSearchText] = useState("");
  const [nextPage, setNextPage] = useState("");
  const [previousPage, setPreviousPage] = useState("");
  const [page, setPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [searchData, setSearchData] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { isLoading, refetch, data: allCustomersList } = useQuery(
    [keys.customers, nextPage, previousPage],
    () => getAllCustomers({ nextPage, previousPage }),
    {
      onSuccess: (data) => {
        // Check if there's more data available
        if (data.has_more !== undefined) {
          setHasMore(data.has_more);
        }
      }
    }
  );

  const latestSearch = useRef("");

  const handleSearch = async (value) => {
    // Stripe search needs at least 3 characters
    if (value.trim().length < 3) return;
    latestSearch.current = value;
    try {
      // Backend matches name OR email in a single Stripe search
      const results = await getAllCustomersSearch({ value });
      // Ignore responses for text the user has since changed
      if (latestSearch.current !== value) return;
      setSearchData(results);
      setIsSearchActive(true);
      setSearchPage(1);
    } catch (error) {
      console.error("Error searching customers:", error);
    }
  };

  const debouncedHandleSearch = useCallback(
    debounce(handleSearch, 500),
    []
  );

  const handleSearchTextChange = (value) => {
    setSearchText(value);
    latestSearch.current = value;
    if (value === "") {
      setSearchData(null);
      setIsSearchActive(false);
      refetch();
      setPage(1);
    } else if (value.trim().length < 3) {
      // Too short to search: drop any pending search and stale results
      debouncedHandleSearch.cancel();
      setSearchData(null);
      setIsSearchActive(false);
    } else {
      debouncedHandleSearch(value);
    }
  };

  useEffect(() => {
    if (searchText === "" && !searchData) {
      refetch();
    }
  }, [searchText, searchData, refetch]);

  const filterCustomers = () => {
    if (searchData) {
      return searchData;
    }
    return allCustomersList?.data || [];
  };

  if (isLoading) {
    return <Spinner />;
  }

  // Search results are paged locally, ten at a time; the plain list is paged
  // through Stripe with cursors. Each has its own bounds.
  const searchHasMore = searchPage * 10 < (searchData?.length || 0);
  // After paging back with ending_before there is always a later page again
  const listHasMore = Boolean(previousPage) || hasMore;

  const handleNextPage = () => {
    if (isSearchActive) {
      if (searchHasMore) setSearchPage(searchPage + 1);
      return;
    }
    const customers = allCustomersList?.data || [];
    if (!customers.length || !listHasMore) return;
    // Clear the other cursor: getAllCustomers lets ending_before win
    setPreviousPage("");
    setNextPage(customers[customers.length - 1].id);
    setPage(page + 1);
  };

  const handlePreviousPage = () => {
    if (isSearchActive) {
      if (searchPage > 1) setSearchPage(searchPage - 1);
      return;
    }
    const customers = allCustomersList?.data || [];
    if (!customers.length || page === 1) return;
    setNextPage("");
    setPreviousPage(customers[0].id);
    setPage(page - 1);
  };

  const paginatedCustomers = () => {
    if (isSearchActive) {
      const startIndex = (searchPage - 1) * 10;
      return filterCustomers().slice(startIndex, startIndex + 10);
    } else {
      return filterCustomers();
    }
  };

  return (
    <Customers
      customers={paginatedCustomers()}
      handleSearchTextChange={handleSearchTextChange}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      page={isSearchActive ? searchPage : page}
      searchText={searchText}
      hasMore={isSearchActive ? searchHasMore : listHasMore}
    />
  );
};

export default CustomersContainer;

